"use client";

import { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import type { SiteNavigation } from "@/lib/types";

// What Header/Footer render before (or if) siteContent/navigation loads -- exactly what was
// previously hardcoded, so a visitor who lands before the Firestore read resolves (or an admin
// who never touches this screen) sees the identical site that existed before this became
// configurable. This is a deliberate, admin-editable snapshot, not a config source of truth.
export const DEFAULT_NAVIGATION: SiteNavigation = {
  navLinks: [
    { label: "Sessions", href: "/services" },
    { label: "Group Classes", href: "/batches" },
    { label: "FAQs", href: "/faq" },
    { label: "About", href: "/about" },
    { label: "Blog", href: "/blog" },
    { label: "Contact", href: "/contact" },
  ],
  footerLinks: [
    { label: "Privacy", href: "/privacy" },
    { label: "Terms", href: "/terms" },
    { label: "Refunds & Cancellations", href: "/refunds" },
  ],
  socialLinks: [
    { platform: "instagram", url: "https://www.instagram.com/hoop_yogi/" },
    { platform: "youtube", url: "https://www.youtube.com/@HoopYogaAndLife" },
    { platform: "linkedin", url: "https://in.linkedin.com/company/hnyfitness" },
  ],
};

const CACHE_KEY = "siteNavigationCache";

function readCache(): SiteNavigation | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as SiteNavigation) : null;
  } catch {
    return null;
  }
}

/**
 * Nav links, footer links, and social links -- admin-editable (see /admin/content), but the
 * page shell itself stays fully static: this only ever runs as a client-side Firestore read
 * after the static HTML has already painted, with the hardcoded defaults above rendered
 * instantly first so there's never a blank nav. A sessionStorage cache means a returning visitor
 * in the same tab session doesn't even re-fetch. See docs -- this is deliberately NOT a
 * server-rendered read, to keep every public page servable straight from the CDN with zero
 * Cloud Run invocation (same reasoning as AnnouncementBannerDisplay).
 */
export function useSiteNavigation(): SiteNavigation {
  const [nav, setNav] = useState<SiteNavigation>(
    () => (typeof window !== "undefined" && readCache()) || DEFAULT_NAVIGATION
  );

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const snap = await getDoc(doc(db, "siteContent", "navigation"));
        if (cancelled || !snap.exists()) return;
        const data = snap.data() as Partial<SiteNavigation>;
        const merged: SiteNavigation = {
          navLinks: data.navLinks ?? DEFAULT_NAVIGATION.navLinks,
          footerLinks: data.footerLinks ?? DEFAULT_NAVIGATION.footerLinks,
          socialLinks: data.socialLinks ?? DEFAULT_NAVIGATION.socialLinks,
        };
        setNav(merged);
        try {
          sessionStorage.setItem(CACHE_KEY, JSON.stringify(merged));
        } catch {
          // Private browsing / storage disabled -- the fetch itself still succeeded, just isn't
          // cached for next time. Not worth surfacing.
        }
      } catch {
        // Defaults are already rendered -- a Firestore hiccup here just means this visit shows
        // the last-known-good nav instead of the very latest edit.
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  return nav;
}
